// API Configuration and Service Layer

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://99text.com/fax-api";

// Helper function to get auth token
const getAuthToken = () => {
  return localStorage.getItem("authToken");
};

// Helper function to create headers
const createHeaders = (
  includeAuth = true,
  contentType = "application/json"
) => {
  const headers = {
    "Content-Type": contentType,
    "User-Agent": "FaxApp/1.0",
  };

  if (includeAuth) {
    const token = getAuthToken();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
  }

  return headers;
};

// Helper function to handle logout on auth errors
const handleAuthError = () => {
  localStorage.removeItem("authToken");
  localStorage.removeItem("userData");
  localStorage.removeItem("userRole");

  // Dispatch a custom event that the auth context can listen to
  window.dispatchEvent(
    new CustomEvent("authError", {
      detail: { message: "Session expired" },
    })
  );

  // Redirect to login page if not already there
  if (window.location.hash !== "#/login") {
    window.location.hash = "#/login";
  }
};

// Helper function to handle API responses
const handleResponse = async (response) => {
  // Handle authentication errors
  if (response.status === 401 || response.status === 403) {
    console.warn("Authentication error:", response.status);
    handleAuthError();
    throw new Error("Session expired. Please login again.");
  }

  const contentType = response.headers.get("content-type");

  if (!response.ok) {
    let errorMessage = "An error occurred";

    if (contentType && contentType.includes("application/json")) {
      const errorData = await response.json();
      errorMessage = errorData.message || errorData.error || errorMessage;
    } else {
      errorMessage = (await response.text()) || errorMessage;
    }

    throw new Error(errorMessage);
  }

  if (contentType && contentType.includes("application/json")) {
    return response.json();
  } else {
    return response.blob();
  }
};

// API Service Class
class ApiService {
  // Authentication APIs
  static async login(credentials) {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: "POST",
        headers: createHeaders(false),
        body: JSON.stringify({
          username: credentials.username,
          password: credentials.password,
        }),
      });

      const data = await handleResponse(response);

      // Store the token and user data
      if (data.access_token) {
        localStorage.setItem("authToken", data.access_token);
        localStorage.setItem("userData", JSON.stringify(data.user));
        localStorage.setItem("userRole", data.user.userType);
      }

      return {
        token: data.access_token,
        user: data.user,
        role: data.user.userType,
        username: data.user.username,
        email: data.user.email,
        fullName: `${data.user.firstName} ${data.user.lastName}`.trim(),
        firstName: data.user.firstName,
        lastName: data.user.lastName,
      };
    } catch (error) {
      console.error("Login error:", error);
      throw error;
    }
  }

  static async logout() {
    try {
      // If there's a logout endpoint on the server
      await fetch(`${API_BASE_URL}/auth/logout`, {
        method: "POST",
        headers: createHeaders(true),
      });

      // Clear local storage regardless of server response
      localStorage.removeItem("authToken");
      localStorage.removeItem("userData");
      localStorage.removeItem("userRole");

      return true;
    } catch (error) {
      // Clear local storage even if server request fails
      localStorage.removeItem("authToken");
      localStorage.removeItem("userData");
      localStorage.removeItem("userRole");
      console.warn("Logout request failed, but local data cleared:", error);
      return true;
    }
  }

  // Fax APIs
  static async getFaxNumbers() {
    try {
      const response = await fetch(`${API_BASE_URL}/fax/numbers/list`, {
        method: "GET",
        headers: createHeaders(true),
      });

      return await handleResponse(response);
    } catch (error) {
      console.error("Get fax numbers error:", error);
      throw error;
    }
  }

  static async sendFax(faxData) {
    try {
      // Validate required fields
      if (!faxData.toNumber) {
        throw new Error("Recipient number is required");
      }

      if (!faxData.fromNumber) {
        throw new Error("From number is required");
      }

      if (!faxData.file) {
        throw new Error("A file is required to send a fax");
      }

      const formData = new FormData();
      formData.append("toNumber", faxData.toNumber);
      formData.append("fromNumber", faxData.fromNumber);
      formData.append("file", faxData.file);

      // Create headers without Content-Type for FormData (browser will set it with boundary)
      const headers = createHeaders(true);
      delete headers["Content-Type"];

      const response = await fetch(`${API_BASE_URL}/fax/send`, {
        method: "POST",
        headers: headers,
        body: formData,
      });

      return await handleResponse(response);
    } catch (error) {
      console.error("Send fax error:", error);
      throw error;
    }
  }

  static async getSentFaxes(page = 1, limit = 10, search = "") {
    try {
      const offset = (page - 1) * limit;
      const params = new URLSearchParams({
        offset: offset.toString(),
        limit: limit.toString(),
      });

      // Add search parameter if provided
      if (search) {
        params.append("search", search);
      }

      const response = await fetch(`${API_BASE_URL}/fax/history?${params}`, {
        method: "GET",
        headers: createHeaders(true),
      });

      return await handleResponse(response);
    } catch (error) {
      console.error("Get sent faxes error:", error);
      throw error;
    }
  }

  static async searchSentFaxes(fromNumber = "", date = "") {
    try {
      const params = new URLSearchParams();

      if (fromNumber) {
        params.append("fromNumber", fromNumber);
      }

      if (date) {
        params.append("date", date);
      }

      const response = await fetch(`${API_BASE_URL}/fax/search?${params}`, {
        method: "GET",
        headers: createHeaders(true),
      });

      return await handleResponse(response);
    } catch (error) {
      console.error("Search sent faxes error:", error);
      throw error;
    }
  }

  static async downloadFaxPdf(mediaUrl, fileName = "fax.pdf") {
    try {
      const response = await fetch(mediaUrl, {
        method: "GET",
        headers: createHeaders(true),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Return the blob for download
      const blob = await response.blob();

      // Create download link
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      return true;
    } catch (error) {
      console.error("Download fax PDF error:", error);
      throw error;
    }
  }

  static async viewFaxPdf(mediaUrl) {
    try {
      // For viewing, we can directly open the mediaUrl in a new tab
      // since it's a publicly accessible URL
      window.open(mediaUrl, "_blank");
      return true;
    } catch (error) {
      console.error("View fax PDF error:", error);
      throw error;
    }
  }

  static async getReceivedFaxes(page = 1, limit = 10, search = "") {
    try {
      const offset = (page - 1) * limit;
      const params = new URLSearchParams({
        offset: offset.toString(),
        limit: limit.toString(),
      });

      const response = await fetch(
        `${API_BASE_URL}/incoming-fax/list?${params}`,
        {
          method: "GET",
          headers: createHeaders(true),
        }
      );

      return await handleResponse(response);
    } catch (error) {
      console.error("Get received faxes error:", error);
      throw error;
    }
  }

  static async searchReceivedFaxes(toNumber = "", date = "") {
    try {
      const params = new URLSearchParams();

      if (toNumber) {
        params.append("toNumber", toNumber);
      }

      if (date) {
        params.append("date", date);
      }

      const response = await fetch(
        `${API_BASE_URL}/incoming-fax/search?${params}`,
        {
          method: "GET",
          headers: createHeaders(true),
        }
      );

      return await handleResponse(response);
    } catch (error) {
      console.error("Search received faxes error:", error);
      throw error;
    }
  }

  static async markFaxAsRead(faxId) {
    try {
      const response = await fetch(`${API_BASE_URL}/fax/${faxId}/mark-read`, {
        method: "PUT",
        headers: createHeaders(true),
      });

      return await handleResponse(response);
    } catch (error) {
      console.error("Mark fax as read error:", error);
      throw error;
    }
  }

  static async downloadFax(faxId) {
    try {
      const response = await fetch(`${API_BASE_URL}/fax/${faxId}/download`, {
        method: "GET",
        headers: createHeaders(true),
      });

      if (!response.ok) {
        throw new Error("Failed to download fax");
      }

      return response.blob();
    } catch (error) {
      console.error("Download fax error:", error);
      throw error;
    }
  }

  // Admin User Management APIs
  static async getUsers(page = 1, limit = 10, search = "") {
    try {
      // The API endpoint is /users not /admin/users based on the curl example
      let url = `${API_BASE_URL}/users`;

      // Add query parameters if needed
      const params = new URLSearchParams();
      if (page > 1) params.append("page", page.toString());
      if (limit !== 10) params.append("limit", limit.toString());
      if (search) params.append("search", search);

      if (params.toString()) {
        url += `?${params}`;
      }

      const response = await fetch(url, {
        method: "GET",
        headers: createHeaders(true),
      });

      const data = await handleResponse(response);

      // Transform the data to match expected format
      // The API returns an array directly, so we need to wrap it
      return {
        users: Array.isArray(data)
          ? data.map((user) => ({
              id: user.id,
              username: user.username,
              email: user.email,
              fullName: `${user.firstName} ${user.lastName}`.trim(),
              firstName: user.firstName,
              lastName: user.lastName,
              role: user.userType || "user", // Map userType to role
              active: user.isActive,
              createdAt: user.createdAt,
              updatedAt: user.updatedAt,
            }))
          : [],
        total: Array.isArray(data) ? data.length : 0,
      };
    } catch (error) {
      console.error("Get users error:", error);
      throw error;
    }
  }

  static async createUser(userData) {
    try {
      // Transform the data to match API expectations
      const apiData = {
        username: userData.username,
        email: userData.email,
        firstName: userData.fullName?.split(" ")[0] || userData.firstName || "",
        lastName:
          userData.fullName?.split(" ").slice(1).join(" ") ||
          userData.lastName ||
          "",
        password: userData.password,
        userType: userData.role || "user",
      };

      const response = await fetch(`${API_BASE_URL}/auth/register`, {
        method: "POST",
        headers: createHeaders(true),
        body: JSON.stringify(apiData),
      });

      return await handleResponse(response);
    } catch (error) {
      console.error("Create user error:", error);
      throw error;
    }
  }

  static async updateUser(userId, userData) {
    try {
      // Transform the data to match API expectations
      const apiData = {
        username: userData.username,
        email: userData.email,
        firstName: userData.fullName?.split(" ")[0] || userData.firstName || "",
        lastName:
          userData.fullName?.split(" ").slice(1).join(" ") ||
          userData.lastName ||
          "",
        isActive: userData.active !== undefined ? userData.active : true,
      };

      // Only include password if it's provided
      if (userData.password) {
        apiData.password = userData.password;
      }

      const response = await fetch(`${API_BASE_URL}/users/${userId}`, {
        method: "PATCH",
        headers: createHeaders(true),
        body: JSON.stringify(apiData),
      });

      return await handleResponse(response);
    } catch (error) {
      console.error("Update user error:", error);
      throw error;
    }
  }

  static async deleteUser(userId) {
    try {
      const response = await fetch(`${API_BASE_URL}/users/${userId}`, {
        method: "DELETE",
        headers: createHeaders(true),
      });

      return await handleResponse(response);
    } catch (error) {
      console.error("Delete user error:", error);
      throw error;
    }
  }

  static async toggleUserStatus(userId, active) {
    try {
      // This might need to be adjusted based on actual API endpoint
      const response = await fetch(`${API_BASE_URL}/users/${userId}/status`, {
        method: "PUT",
        headers: createHeaders(true),
        body: JSON.stringify({ isActive: active }),
      });

      return await handleResponse(response);
    } catch (error) {
      console.error("Toggle user status error:", error);
      throw error;
    }
  }

  // Utility method to check if user is authenticated
  static isAuthenticated() {
    const token = getAuthToken();
    const userData = localStorage.getItem("userData");
    return !!(token && userData);
  }

  // Utility method to get current user data
  static getCurrentUser() {
    try {
      const userData = localStorage.getItem("userData");
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error("Error parsing user data:", error);
      return null;
    }
  }
}

export default ApiService;
